import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Pagination,
  IconButton,
  styled,
  CircularProgress,
  Autocomplete,
  TextField,
  Modal,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  keyframes,
  Chip,
} from '@mui/material'
import {
  faMapMarkerAlt,
  faBuilding,
  faClock,
  faEuroSign,
  faHeart,
  faShare,
  faMagnifyingGlass,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faWhatsapp,
  faFacebook,
  faLinkedin,
  faTwitter,
} from '@fortawesome/free-brands-svg-icons'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { SectionsHeading } from '../../../../components/headings'
import AppPagination from '../../../../components/paginations'

import moment from 'moment/moment'
import { debounce } from '../../../../config'
import Api from '../../../../services/api.service'
import { handlingErrors } from '../../../../utils'
import { toast, ToastContainer, Slide } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import lightTheme from '../../../../styles/theme/lightTheme'
import { ROLE_CANDIDAT, TYPE_CONTRAT, TYPE_FILTER } from '../../../../data'
import { useSelector } from 'react-redux'
import JobFilterPanel from './components/filters'

// Pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`

const LiveBadge = styled(Chip)(({ theme }) => ({
  height: 10,
  minWidth: 10,
  fontSize: '10px',
  fontWeight: 600,
  backgroundColor: '#4CAF50',
  color: '#FFFFFF',
  marginRight: 8,
  animation: `${pulse} 1.5s infinite`,
  '& .MuiChip-label': {
    padding: '0 4px',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px',
  },
}))

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
  },
  '&:before': {
    content: '""',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px 16px 0 0',
  },
}))

const StyledPagination = styled(Pagination)(({ theme }) => ({
  '& .MuiPaginationItem-root': {
    '&.Mui-selected': {
      backgroundColor: '#667eea',
      color: '#FFFFFF',
    },
  },
}))

const GridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '20px',
}))

const ImageContainer = styled(Box)(({ theme }) => ({
  height: 200,
  borderRadius: '25px 0 25px 0',
  position: 'relative',
  overflow: 'hidden',

  '& .image': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  [theme.breakpoints.up('sm')]: {
    height: 180,
  },

  [theme.breakpoints.up('lg')]: {},
}))

const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderWidth: 0,
  borderRadius: 0,
  overflow: 'hidden',
  fontFamily: 'Century Gothic',
  fontSize: 12,
  lineHeight: '140%',
  color: lightTheme.palette.grey.grey,

  '& .MuiOutlinedInput-root': {
    height: '100%',
    borderRadius: 0,
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& .MuiInputBase-input': {
    padding: '0 14px',
    textAlign: 'center',
  },

  '& .MuiAutocomplete-paper': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginTop: '8px',
    maxHeight: '240px',
    overflow: 'auto',
    border: '1px solid #e0ecef',
  },

  '& .MuiAutocomplete-option': {
    padding: '12px 16px',
    fontSize: 14,
    fontFamily: 'Century Gothic',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#667eea',
      color: '#FFFFFF',
    },
  },

  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },

  [theme.breakpoints.up('lg')]: {
    fontSize: 14,
  },
}))

export default function OffresEmploi() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const user = useSelector((state) => state.userAuth.user)


  // Extract search parameters
  const titre = searchParams.get('titre') || ''
  const ville = searchParams.get('ville') || ''
  const canton = searchParams.get('canton') || ''
  let type = ville ? 'city' : canton ? 'state' : ''

  const [jobs, setJobs] = useState([])
  const [count, setCount] = useState(0)
  const [jobsPerPage, setJobsPerPage] = useState(10)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingCities, setLoadingCities] = useState(false)
  const [search, setSearch] = useState(titre)
  const [villeSearch, setVilleSearch] = useState(ville)
  const [stateSearch, setStateSearch] = useState(canton)
  const [states, setStates] = useState([])
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)

  const [jobTitle, setJobTitle] = useState('')
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState([])
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  const [jobDebounceTimer, setJobDebounceTimer] = useState(null)

  /* const initialized = useRef(false)

  useEffect(() => {
    if (!initialized.current) {
      setSearch(titre)
      setVilleSearch(ville)
      setStateSearch(canton)
      initialized.current = true
    }
  }, [titre, ville, canton]) */

  // Filter options
  const [filter, setFilter] = useState([
    {
      id: 0,
      titre: t('jobOffers.filterType', 'Type de contrat'),
      filtre: ['CDI', 'CDD', 'Freelance', 'Stage'],
    },
    { id: 1, titre: t('jobOffers.cdi', 'CDI'), filtre: 'CDI' },
    { id: 2, titre: t('jobOffers.cdd', 'CDD'), filtre: 'CDD' },
    {
      id: 3,
      titre: t('jobOffers.freelance', 'Freelance'),
      filtre: 'Freelance',
    },
    { id: 4, titre: t('jobOffers.internship', 'Stage'), filtre: 'Stage' },
  ])

  const orderTypeOptions = [
    ...TYPE_FILTER[i18n.language],
  ]

  // Create contract type options with placeholder
  const contractTypeOptions = [
    { id: 0, titre: t('jobOffers.allContracts', 'All contracts') },
    ...TYPE_CONTRAT[i18n.language],
  ]
  const [filterSelect, setFilterSelect] = useState(contractTypeOptions[0])
  const [orderSelect, setOrderSelect] = useState(orderTypeOptions[0])

  useEffect(() => {
    setOrderSelect(orderTypeOptions.find((obj) => obj.id === orderSelect.id))
  }, [orderTypeOptions])

  const handleSearchChange = debounce((event) => {
    const { value } = event.target
    setSearch(value)
  }, 800)

  const handleFilterSelect = (event) => {
    const inputValue = event.target.value
    setFilterSelect({ id: inputValue, titre: inputValue })
  }

  const handleOrderSelect = (event) => {
    const inputValue = event.target.value
    setOrderSelect(inputValue)
  }

  useEffect(() => {
    fetchJobs()
  }, [page, search, villeSearch, stateSearch, filterSelect.id, orderSelect.id])


  const fetchJobs = async () => {
    setLoading(true)

    let cities = villeSearch

    if (type !== "city" && stateSearch) {
      cities = await getCitiesByState(stateSearch, true)
    }

    getJobs(page, search, cities)
  }


  const handlePageChange = (event, page) => {
    setPage(page)
    getJobs(page, search, villeSearch || states)
  }

  const getJobs = async (page, search, ville) => {
    setCount(0)
    setJobsPerPage(10)
    setLoading(true)
    setJobs([])

    let payload = {
      search: search,
      ville: ville,
      page: page,
      status: 1, // Only active jobs
      order: orderSelect.filtre,
      direction: orderSelect.type,
    }

    // Add contract type filter if not "all"
    if (filterSelect.id !== 0) {
      payload.type = filterSelect.id
    }

    // Remove empty values from payload
    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )

    try {
      let result = await Api.besoinEntreprise.listPostes(payload)

      const handling = handlingErrors(result)

      if (handling.status === 401) {
        setLoading(false)
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }

      if (handling.ok) {
        const res = handling.data
        let jobsData = res || {}

        // Extract jobs from the response (object with numeric keys)
        const jobsArray = Object.values(jobsData).filter(
          (item) => typeof item === 'object' && item.id
        )

        // Sort jobs if needed
        if (orderSelect.filtre === 'postedDate') {
          jobsArray.sort((a, b) => {
            const dateA = new Date(a.dateDebut || a.createdAt || a.postedDate)
            const dateB = new Date(b.dateDebut || b.createdAt || b.postedDate)
            return orderSelect.type === 'asc' ? dateA - dateB : dateB - dateA
          })
        } else if (orderSelect.filtre === 'title') {
          jobsArray.sort((a, b) => {
            const titleA =
              a.poste?.titre || a.intitule || a.titre || a.title || ''
            const titleB =
              b.poste?.titre || b.intitule || b.titre || b.title || ''
            return orderSelect.type === 'asc'
              ? titleA.localeCompare(titleB)
              : titleB.localeCompare(titleA)
          })
        }

        setJobs(jobsArray)
        setCount(res.pagination?.total_items || 0)
        setJobsPerPage(res.pagination?.items_per_page || 10)
        setTimeout(() => {
          setLoading(false)
        }, 500)
      } else {
        setLoading(false)
        window.scrollTo(0, 0)
      }
    } catch (error) {
      setLoading(false)
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
      window.scrollTo(0, 0)
    }
  }

  const handleJobClick = (jobId) => {
    navigate(`/offres-emploi/${jobId}`)
  }

  const toggleFavorite = (jobId, event) => {
    event.stopPropagation()
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === jobId ? { ...job, favorite: !job.favorite } : job
      )
    )
  }

  const handleJobChange = (event, newValue) => {
    setSearch(newValue?.value || '')
    //setJobTitleInput(newValue || '')
  }

  const handleClearSearch = () => {
    setSearch(titre)
    setJobTitleSuggestions([])
  }

  const handleShare = (job) => {
    setSelectedJob(job)
    setShareModalOpen(true)
  }

  const handleShareOption = (option) => {
    const jobTitle =
      selectedJob?.poste?.titre || selectedJob?.intitule || selectedJob?.title
    const jobDescription =
      selectedJob?.description || selectedJob?.descriptionPoste
    const jobUrl = `${window.location.origin}/offres-emploi/${selectedJob.id}`

    if (option.url) {
      window.open(option.url, '_blank')
    } else if (option.action) {
      option.action()
    }
    setShareModalOpen(false)
  }

  const getShareOptions = () => {
    const jobTitle =
      selectedJob?.poste?.titre || selectedJob?.intitule || selectedJob?.title
    const jobDescription =
      selectedJob?.description || selectedJob?.descriptionPoste
    const jobUrl = `${window.location.origin}/offres-emploi/${selectedJob?.id}`

    return [
      {
        name: 'WhatsApp',
        url: `https://wa.me/?text=${encodeURIComponent(
          `${jobTitle}\n${jobDescription}\n${jobUrl}`
        )}`,
        icon: faWhatsapp,
        color: '#25D366',
      },
      {
        name: 'Facebook',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          jobUrl
        )}&quote=${encodeURIComponent(jobTitle)}`,
        icon: faFacebook,
        color: '#1877F2',
      },
      {
        name: 'LinkedIn',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          jobUrl
        )}&summary=${encodeURIComponent(jobDescription)}`,
        icon: faLinkedin,
        color: '#0077B5',
      },
      {
        name: 'Twitter',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          `${jobTitle} ${jobUrl}`
        )}`,
        icon: faTwitter,
        color: '#1DA1F2',
      },
      {
        name: t('jobOffers.copyLink'),
        action: () => {
          navigator.clipboard.writeText(jobUrl)
          toast.success(t('jobOffers.linkCopied'), {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
          })
        },
        icon: faShare,
        color: '#667eea',
      },
    ]
  }

  const handleJobTitleChange = async (event, value) => {
    const searchValue = value
    setJobTitle(searchValue)

    // Clear existing timer
    if (jobDebounceTimer) {
      clearTimeout(jobDebounceTimer)
    }

    // Set new timer to search after user stops typing
    const newTimer = setTimeout(async () => {
      if (searchValue && searchValue.length > 2) {
        try {
          const jobs = await getPostes(searchValue)
          const jobSuggestions = jobs.map((job) => ({
            value: job.intitule || job.titre || job.title,
            label: job.intitule || job.titre || job.title,
          }))
          setJobTitleSuggestions(jobSuggestions)
        } catch (error) {
          console.error('Error searching jobs:', error)
          setJobTitleSuggestions([])
        }
      } else {
        setJobTitleSuggestions([])
      }
    }, 500)

    setJobDebounceTimer(newTimer)
  }

  const getPostes = async (searchQuery) => {
    let payload = {
      search: searchQuery,
    }

    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
    setLoadingJobs(true)

    try {
      let result = await Api.poste.listWithFilter({
        status: 1,
        ...payload,
      })

      const handling = handlingErrors(result)

      if (handling.ok) {
        const res = handling.data
        setLoadingJobs(false)
        return res
      } else {
        setLoadingJobs(false)
        return []
      }
    } catch (error) {
      setLoadingJobs(false)
      return []
    }
  }

  const getCitiesByState = async (state, returnOnly = false) => {
    //setLoadingCities(true)
    try {
      const response = await Api.countriesCities.getCitiesByState(
        'Switzerland',
        state
      )

      const cities = response.data.data

      if (!returnOnly) {
        setStates(cities)
      }

      return cities
    } catch (error) {
      console.error(error)
      return []
    } finally {
      //setLoadingCities(false)
    }
  }

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleLocationChange = (value) => setSelectedLocation(value);
  const handleStatusChange = (value) => setSelectedStatus(value);
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  let villeText = ville || canton
  const handleClearFilters = () => {
    setPage(1)
    handleClearSearch()
    setSelectedLocation(null);
    setSelectedStatus(null);
    setOrderSelect(orderTypeOptions[0]);
    setFilterSelect(contractTypeOptions[0]);
    setStartDate(null);
    setEndDate(null);
    setCity({ name: villeText, type: type })
    if (type === 'city') { setVilleSearch(villeText) } else {
      setStateSearch(villeText)
      getCitiesByState(canton)
    }
  };

  const clearFilterSelect = () => {
    setFilterSelect(contractTypeOptions[0]);
  };

  const [city, setCity] = useState({ name: villeText, type: type })
  const [cityInput, setCityInput] = useState('')
  const [citySuggestions, setCitySuggestions] = useState([])
  const [stateSuggestions, setStateSuggestions] = useState([])
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [open, setOpen] = useState(false);

  // Get capital city of selected country on component mount
  useEffect(() => {
    handleCity()
    getCapitalCity()

  }, [])

  const getCapitalCity = () => {
    const countryCode = localStorage.getItem('codePays') || 'fr'

    // Capital cities mapping
    const capitalCities = {
      fr: 'Paris',
      en: 'London',
      de: 'Berlin',
      es: 'Madrid',
      it: 'Rome',
      pt: 'Lisbon',
      be: 'Brussels',
      ch: 'Bern',
      nl: 'Amsterdam',
      lu: 'Luxembourg',
      ca: 'Ottawa',
      us: 'Washington',
      au: 'Canberra',
    }

    const capital = capitalCities[countryCode] || 'Paris'
    //setCity({ name: capital, type: 'city' })
    setCityInput(capital)
  }


  const handleCityChange = (event, newValue) => {
    setCity(newValue || '')
    if (newValue?.type === 'state') {
      setStateSearch(newValue.name)
      setVilleSearch('')
      getCitiesByState(newValue.name)
    }
    if (newValue?.type === 'city') {
      setVilleSearch(newValue.name)
      setStateSearch('')
    }
    if (!newValue) {
      setVilleSearch('')
      setStateSearch('')
    }
  }


  const handleCityInputChange = (event, value) => {
    //setCityInput(value);
    setLoadingCities(true)
    setFilteredOptions([])

    if (!value) {
      setFilteredOptions([...stateSuggestions, ...citySuggestions]);
      setLoadingCities(false)
      return;
    }

    const lowerValue = value.toLowerCase();


    const filtered = [...stateSuggestions, ...citySuggestions].filter(
      (option) =>
        option.name.toLowerCase().includes(lowerValue)
    );

    setTimeout(() => {
      setFilteredOptions(filtered);
      setLoadingCities(false)
    }, 100);
  };

  const handleCity = async () => {

    setLoadingCities(true)
    try {

      const response = await Api.countriesCities.getCitiesByCountry(
        'Switzerland'
      )
      const response2 = await Api.countriesCities.getStatesByCountry(
        'Switzerland'
      )

      setCitySuggestions(response.data.data.map(city => ({
        name: city,
        type: "city"
      })))
      setStateSuggestions(response2.data.data.states.map(state => ({
        name: state.name,
        type: "state"
      })))


    } catch (error) {
      console.error('Error searching cities:', error)
    } finally {
      setLoadingCities(false)
    }
  }

  useEffect(() => {
    const allOptions = [...stateSuggestions, ...citySuggestions];
    setFilteredOptions(allOptions);
  }, [stateSuggestions, citySuggestions]);

  return (
    <main>
      <Box
        width={'100%'}
        component={'section'}
        sx={{
          padding: { xs: '40px 0 130px 0', md: '100px 0 ', lg: '80px 0' },
        }}
      >
        <Box width={'100%'} position={'relative'}>
          <Container
            maxWidth={'lg'}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '40px',
            }}
          >
            <SectionsHeading type={'animation'}>
              {t('jobOffers.title')}
            </SectionsHeading>

            <Typography variant="body" sx={{ fontSize: { xs: 12, md: 18 } }}>
              {t('jobOffers.description')}
            </Typography>

            <JobFilterPanel
              search={search}
              handleJobChange={handleJobChange}
              handleJobTitleChange={handleJobTitleChange}
              jobTitleSuggestions={jobTitleSuggestions}
              loadingJobs={loadingJobs}
              contractTypeOptions={contractTypeOptions}
              filterSelect={filterSelect}
              handleFilterSelect={handleFilterSelect}
              order={orderTypeOptions}
              orderSelect={orderSelect}
              handleOrderSelect={handleOrderSelect}
              location={selectedLocation}
              handleLocationChange={handleLocationChange}
              status={selectedStatus}
              handleStatusChange={handleStatusChange}
              startDate={startDate}
              endDate={endDate}
              handleDateRangeChange={handleDateRangeChange}
              handleClearFilters={handleClearFilters}
              handleClearSearch={handleClearSearch}
              clearFilterSelect={clearFilterSelect}
              filteredOptions={filteredOptions}
              city={city}
              handleCityChange={handleCityChange}
              handleCityInputChange={handleCityInputChange}
              loading={loadingCities}
              citySuggestions={citySuggestions}
              stateSuggestions={stateSuggestions}
              cityInput={cityInput}
              open={open}
              setOpen={setOpen}
            />

            {/* <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item lg={3} md={4} xs={12}>
                <SelectInputBasic
                  type={'border'}
                  items={contractTypeOptions}
                  value={filterSelect.id}
                  handleChange={handleFilterSelect}
                />
              </Grid>
              <Grid item lg={3} md={4} xs={12}>
                <SelectInputObject
                  type={'border'}
                  items={order}
                  value={orderSelect}
                  handleChange={handleOrderSelect}
                />
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  display: { xs: 'none', md: 'none', lg: 'initial' },
                }}
              ></Grid>
              <Grid item lg={4} md={4} xs={12}>
                <CustomAutocomplete
                  freeSolo
                  options={jobTitleSuggestions}
                  value={search}
                  onChange={handleJobChange}
                  onInputChange={handleJobTitleChange}
                  loading={loadingJobs}
                  noOptionsText={
                    loadingJobs
                      ? t('home.loading')
                      : jobTitleSuggestions.length === 0 && search.length > 2
                        ? t('home.noJobsFound')
                        : ''
                  }
                  placeholder={t('home.jobTitlePlaceholder')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={`${t('filter.searchText')}...`}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            {params.InputProps.startAdornment}
                            {loadingJobs && (
                              <div
                                style={{
                                  padding: '0 8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: '#666',
                                  position: 'absolute',
                                  right: 0,
                                }}
                              >
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid #666',
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    animation: 'spin 1s linear infinite',
                                    borderRadius: '50%',
                                    borderRight: '2px solid transparent',
                                    borderColor: '#666 transparent',
                                    borderStyle: 'solid',
                                  }}
                                ></div>
                              </div>
                            )}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {search && (
                              <IconButton
                                disableRipple
                                type={'button'}
                                aria-label={'clear'}
                                onClick={handleClearSearch}
                                sx={{
                                  padding: '8px',
                                  marginRight: '4px',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  style={{
                                    color: '#9ca3af',
                                    fontSize: '16px',
                                  }}
                                />
                              </IconButton>
                            )}
                            {!loadingJobs && (
                              <IconButton
                                disableRipple
                                type={'button'}
                                aria-label={'search'}
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  padding: '8px',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faMagnifyingGlass}
                                  style={{
                                    color: lightTheme.palette.primary.main,
                                    fontSize: '20px',
                                  }}
                                />
                              </IconButton>
                            )}
                          </>
                        ),
                        style: {
                          padding: '10px 15px',
                          height: '100%',
                        },
                      }}
                      sx={{
                        width: '100%',
                        height: { xs: 38, sm: 41, lg: 45 },
                        '& .MuiOutlinedInput-root': {
                          height: '100%',
                          borderRadius: '5px',
                          border: `1px solid ${lightTheme.palette.grey.main}`,
                          backgroundColor: '#FFFFFF',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '0',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                          },
                        },
                        '& .MuiInputBase-input': {
                          flex: 1,
                          color: lightTheme.palette.text.primary,
                          fontFamily: 'Century Gothic',
                          fontSize: 14,
                          lineHeight: '140%',
                          letterSpacing: '0.2px',
                          padding: '0',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid> */}

            {loading ? (
              <Grid container>
                <GridItem item lg={12} alignItems={'center'}>
                  <CircularProgress />
                </GridItem>
              </Grid>
            ) : (
              <>
                <Grid container spacing={5}>
                  {Object.values(jobs)
                    .filter((item) => typeof item === 'object' && item.id)
                    .map((job, index) => (
                      <GridItem
                        item
                        xs={12}
                        sm={6}
                        lg={4}
                        key={job.id}
                        sx={{
                          position: 'relative',
                        }}
                      >
                        <StyledCard>
                          <CardContent
                            sx={{ flexGrow: 1, p: 3, position: 'relative' }}
                          >
                            {/* Job Header */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={3}
                            >
                              <Box sx={{ flex: 1, mr: 2 }}>
                                <Typography
                                  variant="h5"
                                  component="h2"
                                  gutterBottom
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    lineHeight: 1.3,
                                    color: '#1a1a1a',
                                    mb: 1,
                                  }}
                                >
                                  {user &&
                                    user.data.role !== ROLE_CANDIDAT &&
                                    job.postuler && (
                                      <LiveBadge label={' '} />
                                    )}{' '}
                                  {job.poste?.titre}
                                </Typography>

                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 0.5,
                                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                    borderRadius: '20px',
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#667eea',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {job.entreprise?.nom ||
                                      job.company ||
                                      t('contact.company')}
                                  </Typography>
                                </Box>

                                {user &&
                                  user.data.role === ROLE_CANDIDAT &&
                                  job.candidateMatchingScore ? (
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      px: 2,
                                      py: 0.5,
                                      backgroundColor:
                                        job.candidateMatchingScore >= 80
                                          ? 'rgba(16, 185, 129, 0.1)'
                                          : job.candidateMatchingScore >= 60
                                            ? 'rgba(245, 158, 11, 0.1)'
                                            : 'rgba(239, 68, 68, 0.1)',
                                      borderRadius: '20px',
                                      mb: 1,
                                      ml: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color:
                                          job.candidateMatchingScore >= 80
                                            ? '#10b981'
                                            : job.candidateMatchingScore >= 60
                                              ? '#f59e0b'
                                              : '#ef4444',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {Math.round(job.candidateMatchingScore)}%
                                      Match
                                    </Typography>
                                  </Box>
                                ) : (
                                  <></>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => toggleFavorite(job.id, e)}
                                sx={{
                                  color: job.favorite ? '#ef4444' : '#9ca3af',
                                  backgroundColor: job.favorite
                                    ? 'rgba(239, 68, 68, 0.1)'
                                    : 'rgba(156, 163, 175, 0.1)',
                                  '&:hover': {
                                    backgroundColor: job.favorite
                                      ? 'rgba(239, 68, 68, 0.2)'
                                      : 'rgba(156, 163, 175, 0.2)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <FontAwesomeIcon icon={faHeart} />
                              </IconButton>
                            </Box>

                            {/* Job Details */}
                            <Box
                              sx={{
                                display: 'grid',
                                gap: 1.5,
                                mb: 3,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 1.5,
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  borderRadius: '8px',
                                  transition: 'background-color 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                    mr: 2,
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    style={{
                                      color: '#667eea',
                                      fontSize: '14px',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {job.lieu ||
                                    job.ville ||
                                    job.location ||
                                    'Remote'}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 1.5,
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  borderRadius: '8px',
                                  transition: 'background-color 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    mr: 2,
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faBuilding}
                                    style={{
                                      color: '#10b981',
                                      fontSize: '14px',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {job.typeContrat || job.type || 'CDI'}
                                </Typography>
                              </Box>
                              {job.salaireSur && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 32,
                                      height: 32,
                                      borderRadius: '8px',
                                      backgroundColor:
                                        'rgba(251, 191, 36, 0.1)',
                                      mr: 2,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faEuroSign}
                                      style={{
                                        color: '#fbbf24',
                                        fontSize: '14px',
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: '#374151',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {job.devise?.toUpperCase() || 'CHF'} {job.salaire}
                                    /{t('box.mounth')}
                                  </Typography>
                                </Box>
                              )}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 1.5,
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  borderRadius: '8px',
                                  transition: 'background-color 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                    mr: 2,
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faClock}
                                    style={{
                                      color: '#8b5cf6',
                                      fontSize: '14px',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {t('jobOffers.postedOn')}{' '}
                                  {moment(job.date)
                                    .tz('Europe/Paris')
                                    .format('DD/MM/YYYY')}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Description */}
                            {job.avantagesEntreprise &&
                              job.avantagesEntreprise.length > 0 && (
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor:
                                      'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                    borderRadius: '12px',
                                    border:
                                      '1px solid rgba(102, 126, 234, 0.1)',
                                    mb: 3,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: '#4b5563',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {job.avantagesEntreprise.join(', ')}
                                  </Typography>
                                </Box>
                              )}

                            {/* Tags */}
                            {job.competences && job.competences.length > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  mb: 3,
                                }}
                              >
                                {job.competences
                                  .slice(0, 3)
                                  .map((competence, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        px: 2,
                                        py: 0.75,
                                        backgroundColor:
                                          'rgba(99, 102, 241, 0.08)',
                                        border:
                                          '1px solid rgba(99, 102, 241, 0.2)',
                                        borderRadius: '16px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          backgroundColor:
                                            'rgba(99, 102, 241, 0.12)',
                                          transform: 'translateY(-1px)',
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#6366f1',
                                          fontSize: '0.75rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                        }}
                                      >
                                        {competence.nom || competence}
                                      </Typography>
                                    </Box>
                                  ))}
                                {job.competences.length > 3 && (
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 0.75,
                                      backgroundColor:
                                        'rgba(156, 163, 175, 0.08)',
                                      border:
                                        '1px solid rgba(156, 163, 175, 0.2)',
                                      borderRadius: '16px',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#9ca3af',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      +{job.competences.length - 3}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Additional Info */}
                            {(job.horaires ||
                              job.teletravailPropose ||
                              job.delaisRecrutement ||
                              job.avantages ||
                              job.softskils ||
                              job.valeurs) && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: 'rgba(243, 244, 246, 0.8)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(229, 231, 235, 0.5)',
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#374151',
                                      fontSize: '0.9rem',
                                      mb: 2,
                                    }}
                                  >
                                    {t('box.moreInformations')}
                                  </Typography>

                                  {job.horaires && (
                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>
                                          {t('jobOffers.hours', 'Horaires')}:
                                        </strong>{' '}
                                        {job.horaires}
                                      </Typography>
                                    </Box>
                                  )}

                                  {job.teletravailPropose !== undefined && (
                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>
                                          {t('jobOffers.remoteWork')}:
                                        </strong>{' '}
                                        {`${job.teletravailPropose} ${t(
                                          'box.days'
                                        )}`}
                                      </Typography>
                                    </Box>
                                  )}

                                  {job.delaisRecrutement && (
                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>
                                          {t('jobOffers.recruitmentTime')}:
                                        </strong>{' '}
                                        {job.delaisRecrutement}
                                      </Typography>
                                    </Box>
                                  )}

                                  {job.avantages && (
                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>{t('filter.benefits')}:</strong>{' '}
                                        {job.avantages}
                                      </Typography>
                                    </Box>
                                  )}

                                  {job.softskils && job.softskils.length > 0 && (
                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>{t('filter.softskils')}:</strong>{' '}
                                        {job.softskils.join(', ')}
                                      </Typography>
                                    </Box>
                                  )}

                                  {job.valeurs && job.valeurs.length > 0 && (
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        <strong>{t('filter.values')}:</strong>{' '}
                                        {job.valeurs.join(', ')}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                          </CardContent>

                          <CardActions
                            sx={{
                              p: 2,
                              pt: 0,
                              display: 'flex',
                              gap: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="medium"
                              onClick={() => handleJobClick(job.id)}
                              sx={{
                                flexGrow: 1,
                                py: 1.25,
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                background:
                                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow:
                                  '0 4px 14px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background:
                                    'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                                  boxShadow:
                                    '0 6px 20px rgba(102, 126, 234, 0.4)',
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              {t('jobOffers.viewDetails')}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleShare(job)
                              }}
                              sx={{
                                ml: 1,
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(156, 163, 175, 0.1)',
                                border: '1px solid rgba(156, 163, 175, 0.2)',
                                color: '#6b7280',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(156, 163, 175, 0.2)',
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              <FontAwesomeIcon icon={faShare} />
                            </IconButton>
                          </CardActions>
                        </StyledCard>
                      </GridItem>
                    ))}
                </Grid>

                {/* No Results */}
                {count === 0 && (
                  <Box textAlign="center" py={8}>
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      {t('jobOffers.noResults', 'Aucune offre trouvée')}
                    </Typography>
                    <Typography variant="body2">
                      {t(
                        'jobOffers.tryDifferentFilters',
                        'Essayez avec des filtres différents'
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Pagination */}
                {count > jobsPerPage && (
                  <Box display="flex" justifyContent="center" mt={4}>
                    <AppPagination
                      count={count}
                      page={page}
                      pageSize={jobsPerPage}
                      handlePageChange={handlePageChange}
                    />
                  </Box>
                )}
              </>
            )}
          </Container>

          {/* Share Modal */}
          {shareModalOpen && (
            <Modal
              open={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              aria-labelledby="share-modal-title"
              aria-describedby="share-modal-description"
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  minWidth: '320px',
                  maxWidth: '400px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                }}
              >
                <Typography
                  id="share-modal-title"
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    textAlign: 'center',
                    color: '#1a1a1a',
                  }}
                >
                  {t('jobOffers.shareOffer')}
                </Typography>

                <List sx={{ py: 1 }}>
                  {getShareOptions().map((option, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleShareOption(option)}
                      sx={{
                        borderRadius: '8px',
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <FontAwesomeIcon
                          icon={option.icon}
                          style={{
                            color: option.color,
                            fontSize: '20px',
                            width: '20px',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={option.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 500,
                            color: '#374151',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShareModalOpen(false)}
                    sx={{
                      borderColor: '#e5e7eb',
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                      },
                    }}
                  >
                    {t('button.cancel')}
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
        </Box>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </main>
  )
}
